import React, {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useState } from "react";
import { useQuery } from "react-query";
import {
  BasePagination,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { getUserPrevilegeList, getUserTotalList } from "api/userAPI";
import { Checkbox, TableColumnsType } from "antd";
import { matchStringSearchKeyword } from "utils/functions";
import { UserDto } from "api/interfaces/userInterface.interface";
import { AlertRuleRecipients } from "api/interfaces/alertInterface.interface";
import {
  getHeaderCheckboxState,
  createCellToggleHandler,
  createHeaderToggleHandler,
} from "utils/UserUtil";
import {
  SearchVirtualTable,
  SearchVirtualTableRecord,
} from "./SearchVirtualTable";

export type SearchUserProps = {
  accountId: string;
  keyword: string;
  selectedUserId?: string[];
  onSelectedRowsChangeUser?: (selectedRows: string[]) => void;
  onChangeEnableCheckUserList?: (selectedRows: AlertRuleRecipients[]) => void;
  isSupportEmail?: boolean;
  userGroupId?: string;
  enableCheckUserList?: AlertRuleRecipients[];
  isEditUserGroup?: boolean;
  previlege?: string;
};

export interface DataRow extends UserDto, SearchVirtualTableRecord {
  name: string;
}

export const SearchUserList = forwardRef(
  (props: SearchUserProps, ref: ForwardedRef<any>): JSX.Element => {
    // console.log(props.enableCheckUserList);
    const [users, setUsers] = useState<DataRow[]>([]);
    const [pending, setPending] = useState<boolean>(true);

    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const [userQueryInfo] = useState<BasePagination>({
      total: 0,
      pageNum: 0,
      pageLimit: 2000,
      keyword: props.keyword as string,
      sortType: "name",
      sortDirection: SORT_DIRECTION.ASC,
    });

    const data: DataRow[] = useMemo(() => {
      return users.filter((rec) => {
        if (props.keyword) {
          return (
            matchStringSearchKeyword(rec.name, props.keyword) ||
            matchStringSearchKeyword(rec.email, props.keyword)
          );
        } else return true;
      });
    }, [props.keyword, users]);

    const checkDisabledForOnlyOneGroup = ({
      userId,
      userGroups,
      userGroupCount,
    }: UserDto) => {
      if (
        !props.selectedUserId ||
        props.selectedUserId.length > 0 ||
        !props.selectedUserId.includes(userId) ||
        !(userGroupCount > 1)
      ) {
        if (userGroups && userGroups.length > 0) {
          return (
            userGroups.length === 1 &&
            userGroups[0].groupId === props.userGroupId
          );
        }
        return true;
      }
      return false;
    };

    const selectedEnable = useMemo(() => {
      return (data ?? []).map((user: UserDto) => {
        return {
          ...user,
          userId: user.userId,
          enableEmail:
            props.enableCheckUserList?.find(
              (item) => item.userId === user.userId
            )?.enableEmail || false,
          enablePush:
            props.enableCheckUserList?.find(
              (item) => item.userId === user.userId
            )?.enablePush || false,
        };
      });
    }, [props.enableCheckUserList, data]);

    const onCheckRecipient = useCallback(
      (recipients: AlertRuleRecipients[]) => {
        setUsers((prev) =>
          prev.map((user) => {
            const recipient = recipients.find(
              (recipient) => recipient.userId === user.userId
            );
            if (recipient) {
              return {
                ...user,
                enableEmail: recipient.enableEmail,
                enablePush: recipient.enablePush,
              };
            }
            // recipient가 없으면 기존 값 유지
            return user;
          })
        );
        if (props.onChangeEnableCheckUserList) {
          // 기존 enableCheckUserList와 병합하여 업데이트
          // 업데이트할 recipients에 포함된 항목들은 기존 항목에서 제거하고 새로 추가
          const existingRecipients = props.enableCheckUserList || [];
          const updatedUserIds = recipients.map((r) => r.userId);
          const mergedRecipients = [
            ...existingRecipients.filter(
              (existing) => !updatedUserIds.includes(existing.userId)
            ),
            ...recipients,
          ];
          props.onChangeEnableCheckUserList(mergedRecipients);
        }
      },
      [props.onChangeEnableCheckUserList, props.enableCheckUserList]
    );

    // 헤더 체크박스 상태 계산 공통 함수
    const getHeaderCheckboxStateMemo = useCallback(
      ({ field }: { field: "enableEmail" | "enablePush" }) => {
        return getHeaderCheckboxState({ selectedEnable, field });
      },
      [selectedEnable]
    );

    // 개별 체크박스 onChange 핸들러 공통 함수
    const handleCellToggle = useCallback(
      createCellToggleHandler({
        selectedEnable,
        onCheckRecipient,
        getId: (item) => item.userId,
      }),
      [selectedEnable, onCheckRecipient]
    );

    // 헤더 토글 핸들러 공통 함수
    const handleHeaderToggle = useCallback(
      createHeaderToggleHandler({
        filteredData: data,
        allItems: users,
        existingRecipients: props.enableCheckUserList,
        onCheckRecipient,
        getId: (user) => user.userId,
        getRecipientId: (recipient) => recipient.userId,
        createRecipient: (userId, enableEmail, enablePush) =>
          ({
            userId,
            enableEmail,
            enablePush,
          } as AlertRuleRecipients),
      }),
      [data, users, props.enableCheckUserList, onCheckRecipient]
    );

    useQuery(
      ["user", userQueryInfo, props.previlege],
      () =>
        props.previlege
          ? getUserPrevilegeList({
              payload: userQueryInfo,
              accountId: props.accountId,
              previlege: props.previlege,
            })
          : getUserTotalList({
              payload: userQueryInfo,
              accountId: props.accountId,
            }),
      {
        retry: 0,
        refetchOnWindowFocus: false,
        onSuccess: (res: any) => {
          // console.log(res);
          if (!res.result) {
            return;
          }
          setUsers(
            res.result.map((user: UserDto) => {
              return {
                ...user,
                id: user.userId,
                key: user.userId,
              };
            })
          );
        },
        onError: (e: any) => {},
        onSettled: () => {
          setPending(false);
        },
      }
    );
    const onSelectChange = (
      newSelectedRowKeys: React.Key[],
      _selectedRows?: DataRow[]
    ) => {
      setSelectedRowKeys(newSelectedRowKeys);
      if (props.onSelectedRowsChangeUser)
        props.onSelectedRowsChangeUser(newSelectedRowKeys as string[]);
    };

    useEffect(() => {
      if (props.selectedUserId) {
        setSelectedRowKeys(props.selectedUserId);
      }
    }, [props.selectedUserId]);

    const columns: TableColumnsType<DataRow> = useMemo(
      () => [
        {
          dataIndex: "name",
          title: "Name",
          render: (value, row) => (
            <>
              <b>{value}</b>
              <address className="location">{row.email}</address>
            </>
          ),
          ellipsis: true,
        },
        {
          dataIndex: "enableEmail",
          title: ({ filters }) => {
            const { checked, indeterminate } = getHeaderCheckboxStateMemo({
              field: "enableEmail",
            });
            return (
              <div>
                <Checkbox
                  defaultChecked={false}
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={(e) => {
                    handleHeaderToggle({
                      field: "enableEmail",
                      checked: e.target.checked,
                    });
                  }}
                  data-testid={`search-virtual-table-checkbox-enable-email-all`}
                />
                Email
              </div>
            );
          },
          render: (value, row, index) => {
            return (
              <Checkbox
                checked={selectedEnable.some(
                  (item) => item.userId === row.userId && item.enableEmail
                )}
                onChange={() =>
                  handleCellToggle({ field: "enableEmail", id: row.userId })
                }
                data-testid={`search-virtual-table-checkbox-enable-email-${index}`}
              />
            );
          },
          width: 80,
          hidden: !!!props.isSupportEmail,
        },
        {
          key: "enablePush",
          dataIndex: "enablePush",
          title: ({ filters }) => {
            const { checked, indeterminate } = getHeaderCheckboxStateMemo({
              field: "enablePush",
            });
            return (
              <div>
                <Checkbox
                  defaultChecked={false}
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={(e) => {
                    handleHeaderToggle({
                      field: "enablePush",
                      checked: e.target.checked,
                    });
                  }}
                  data-testid={`search-virtual-table-checkbox-enable-push-all`}
                />
                Push
              </div>
            );
          },
          render: (value, row, index) => {
            return (
              <Checkbox
                checked={selectedEnable.some(
                  (item) => item.userId === row.userId && item.enablePush
                )}
                onChange={() =>
                  handleCellToggle({ field: "enablePush", id: row.userId })
                }
                data-testid={`search-virtual-table-checkbox-enable-push-${index}`}
              />
            );
          },
          width: 80,
          hidden: !!!props.isSupportEmail,
        },
      ],
      [
        selectedEnable,
        getHeaderCheckboxStateMemo,
        handleCellToggle,
        handleHeaderToggle,
        props.isSupportEmail,
      ]
    );

    return (
      <SearchVirtualTable<DataRow>
        columns={columns}
        dataSource={data}
        loading={pending}
        isSingleMode={false}
        useExpandable={false}
        useRowExpandedSelection={false}
        selectedRowKeys={!props.isSupportEmail ? selectedRowKeys : []}
        onSelectChange={
          !props.isSupportEmail
            ? (keys, rows) =>
                onSelectChange(keys as string[], rows as DataRow[])
            : undefined
        }
        checkDisabled={
          !props.isSupportEmail && props.isEditUserGroup
            ? checkDisabledForOnlyOneGroup
            : undefined
        }
      />
    );
  }
);
